import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { usePerformance } from '../hooks/usePerformance';
// Text effects not needed in Services

const Services = () => {
    const servicesRef = useRef(null);
    const { isMobile, prefersReducedMotion, isLowEndDevice } = usePerformance();

    const services = [
        {
            id: 1,
            title: "Achat Media",
            description: "Gestion de l'allocation du budget d'achat d'espace publicitaire sur tous supports : TV, Radio, Display, Social Media, OOH/DOOH, Print et plus."
        },
        {
            id: 2,
            title: "Production Audiovisuelle",
            description: "Création de contenus audiovisuels de qualité : spots publicitaires, vidéos institutionnelles, documentaires, films et post-production."
        },
        {
            id: 3,
            title: "Digital Media",
            description: "Gestion des réseaux sociaux, création et gestion de campagnes, SEO & SEA, planification de contenu et optimisation de performance."
        },
        {
            id: 4,
            title: "Consulting & Stratégie",
            description: "Conseils stratégiques en marketing et communication, élaboration de plans média optimisés et recommandations personnalisées."
        },
        {
            id: 5,
            title: "Design & Créativité",
            description: "Création d'identités visuelles, logos, supports imprimés, design web et visuels innovants alignés avec votre marque."
        },
        {
            id: 6,
            title: "Événementielle",
            description: "Organisation d'événements variés, création de contenu événementiel, intégration de technologies innovantes et mesure de performance."
        },
        {
            id: 7,
            title: "Animation 2D & 3D",
            description: "Studio d'animation spécialisé en création 2D et 3D pour publicités, contenus promotionnels et visuels captivants."
        },
        {
            id: 8,
            title: "Influence Marketing",
            description: "Collaboration stratégique avec influenceurs, création de contenus uniques et mesure transparente du ROI."
        }
    ];

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1]
            }
        }
    };

    return (
        <div 
            ref={servicesRef} 
            className="bg-white/50 dark:bg-black/50 backdrop-blur-md min-h-screen flex flex-col overflow-hidden relative py-8 md:py-20"
            id="services"
            itemScope 
            itemType="https://schema.org/Service"
        >
            {/* Vibrant animated background */}
            {!prefersReducedMotion && (() => {
                const orbCount = isMobile || isLowEndDevice ? 3 : 4; // Reduced from 6
                const blurAmount = isMobile || isLowEndDevice ? '50px' : '80px';
                
                return (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        {/* Animated gradient orbs - more vibrant */}
                        {Array.from({ length: orbCount }).map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute rounded-full"
                                style={{
                                    width: `${250 + i * 120}px`,
                                    height: `${250 + i * 120}px`,
                                    background: `radial-gradient(circle, ${
                                        i % 3 === 0 
                                            ? 'rgba(99, 102, 241, 0.6)' 
                                            : i % 3 === 1
                                            ? 'rgba(168, 85, 247, 0.6)'
                                            : 'rgba(139, 92, 246, 0.6)'
                                    } 0%, transparent 70%)`,
                                    filter: `blur(${blurAmount})`,
                                    left: `${15 + i * 15}%`,
                                    top: `${8 + i * 15}%`,
                                    opacity: i % 2 === 0 ? 0.5 : 0.4,
                                    willChange: 'transform, opacity',
                                    contain: 'layout style paint',
                                    transform: 'translateZ(0)',
                                }}
                                animate={{
                                    x: [0, 60, -50, 0],
                                    y: [0, -60, 50, 0],
                                    scale: isMobile ? [1, 1.25, 0.9, 1] : [1, 1.4, 0.8, 1],
                                    opacity: [
                                        i % 2 === 0 ? 0.5 : 0.4,
                                        i % 2 === 0 ? 0.7 : 0.6,
                                        i % 2 === 0 ? 0.3 : 0.25,
                                        i % 2 === 0 ? 0.5 : 0.4,
                                    ],
                                }}
                                transition={{
                                    duration: 12 + i * 2,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: i * 1.5,
                                    type: "tween",
                                }}
                            />
                        ))}
                    </div>
                );
            })()}
            {/* Header - Compact on mobile */}
            <div className="relative z-10 pt-8 md:pt-10 pb-6 md:pb-6 px-4 md:px-0">
                <motion.h2 
                    className="text-center text-3xl md:text-6xl lg:text-7xl font-black tracking-tight"
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                >
                    <span className="text-gray-900 dark:text-white">Nos </span>
                    <span className="text-gray-900 dark:text-white font-black">Expertises</span>
                </motion.h2>
            </div>

            {/* Services Grid - Premium experience */}
            <div className="flex-1 overflow-hidden relative flex items-center pt-4 md:pt-28 pb-4 md:pb-8 z-10">
                {/* Mobile: Horizontal swipe with 3 cards stacked vertically per page */}
                <div className="md:hidden w-full h-full overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide">
                    <div className="flex h-full gap-4 px-4" style={{ width: `${Math.ceil(services.length / 3) * 100}vw` }}>
                        {Array.from({ length: Math.ceil(services.length / 3) }).map((_, groupIndex) => (
                            <div 
                                key={groupIndex} 
                                className="w-[calc(100vw-2rem)] flex-shrink-0 snap-center flex flex-col gap-3 h-full justify-center"
                            >
                                {services.slice(groupIndex * 3, groupIndex * 3 + 3).map((service, cardIndex) => (
                                    <motion.div
                                        key={service.id}
                                        variants={itemVariants}
                                        className="bg-gradient-to-br from-gray-50 to-white dark:from-[#111111] dark:to-[#0a0a0a] border border-gray-200/60 dark:border-[#262626]/60 p-6 rounded-xl shadow-lg backdrop-blur-sm flex-shrink-0"
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, margin: "-30px" }}
                                        transition={{ 
                                            delay: (groupIndex * 3 + cardIndex) * 0.08,
                                            duration: 0.4,
                                            ease: [0.4, 0, 0.2, 1]
                                        }}
                                    >
                                        {/* Number badge - Centered on top */}
                                        <div className="flex items-center justify-center mb-4">
                                            <div 
                                                className="h-14 w-14 bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-800/20 flex items-center justify-center rounded-xl shadow-md"
                                            >
                                                <span className="text-purple-600 dark:text-purple-400 text-2xl font-black">
                                                    {service.id}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {/* Title - Centered */}
                                        <h3 
                                            className="font-black text-xl text-gray-900 dark:text-white text-center mb-4 leading-tight"
                                        >
                                            {service.title}
                                        </h3>
                                        
                                        {/* Description - Centered, larger text */}
                                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed text-center">
                                            {service.description}
                                        </p>
                                    </motion.div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Desktop: Premium grid with smooth animations */}
                <div className="hidden md:grid md:grid-cols-4 gap-5 lg:gap-6 w-full h-full px-6 lg:px-10 overflow-y-auto" style={{ gridTemplateRows: 'repeat(2, 1fr)' }}>
                    {services.map((service, index) => (
                        <motion.div
                            key={service.id}
                            variants={itemVariants}
                            className="bg-gradient-to-br from-gray-50 to-white dark:from-[#111111] dark:to-[#0a0a0a] border border-gray-200/50 dark:border-[#262626]/50 p-5 lg:p-6 group cursor-pointer flex flex-col h-full rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 backdrop-blur-sm"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-50px" }}
                            whileHover={{ scale: 1.03, y: -4, borderColor: "rgba(0,0,0,0.2)", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
                        >
                            <div 
                                className="h-32 lg:h-36 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-[#1a1a1a] dark:to-[#0f0f0f] mb-4 lg:mb-5 flex items-center justify-center rounded-xl flex-shrink-0 shadow-inner group-hover:scale-105 transition-transform duration-300"
                            >
                                <span className="text-gray-500 dark:text-gray-500 text-sm font-semibold">Icon</span>
                            </div>
                            <h3 
                                className="font-bold text-xl lg:text-2xl text-gray-900 dark:text-white mb-3 lg:mb-4 flex-shrink-0 leading-tight"
                            >
                                {service.title}
                            </h3>
                            <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400 font-light leading-relaxed flex-1">
                                {service.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Role & Impact Section (Static, not part of slider) */}
            {/* <div className="flex-shrink-0 bg-gray-50/70 dark:bg-[#0a0a0a]/70 backdrop-blur-sm py-6">
                <div className="m-auto max-w-6xl w-full px-4 md:px-12">
                    <motion.div 
                        className="flex flex-col lg:flex-row gap-8 lg:gap-16 justify-between"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.div 
                            className="lg:w-1/2 flex flex-col justify-center"
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <div className='text-gray-900 dark:text-white mb-4'>
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" className='fill-current'><path d="M2 12h2a7.986 7.986 0 0 1 2.337-5.663 7.91 7.91 0 0 1 2.542-1.71 8.12 8.12 0 0 1 6.13-.041A2.488 2.488 0 0 0 17.5 7C18.886 7 20 5.886 20 4.5S18.886 2 17.5 2c-.689 0-1.312.276-1.763.725-2.431-.973-5.223-.958-7.635.059a9.928 9.928 0 0 0-3.18 2.139 9.92 9.92 0 0 0-2.14 3.179A10.005 10.005 0 0 0 2 12zm17.373 3.122c-.401.952-.977 1.808-1.71 2.541s-1.589 1.309-2.542 1.71a8.12 8.12 0 0 1-6.13.041A2.488 2.488 0 0 0 6.5 17C5.114 17 4 18.114 4 19.5S5.114 22 6.5 22c.689 0 1.312-.276 1.763-.725A9.965 9.965 0 0 0 12 22a9.983 9.983 0 0 0 9.217-6.102A9.992 9.992 0 0 0 22 12h-2a7.993 7.993 0 0 1-.627 3.122z"></path><path d="M12 7.462c-2.502 0-4.538 2.036-4.538 4.538S9.498 16.538 12 16.538s4.538-2.036 4.538-4.538S14.502 7.462 12 7.462zm0 7.076c-1.399 0-2.538-1.139-2.538-2.538S10.601 9.462 12 9.462s2.538 1.139 2.538 2.538-1.139 2.538-2.538 2.538z"></path></svg>
                            </div>
                            <h3 className="text-xl md:text-2xl text-gray-900 dark:text-white font-bold mb-3">
                                Notre <span className='font-black'>Rôle</span>
                            </h3>
                            <p className='text-sm md:text-base text-gray-600 dark:text-gray-300 font-light leading-relaxed'>
                                Au-delà de la gestion conventionnelle du budget d'achat d'espace, Tomorrow va plus loin en collaborant étroitement avec les marques pour bâtir une identité distincte et unique. En qualité de conseiller, nous définissons la recommandation stratégique de communication la plus adaptée.
                            </p>
                        </motion.div>
                        <motion.div 
                            className="lg:w-1/2 flex flex-col justify-center"
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <div className='text-gray-900 dark:text-white mb-4'>
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" className='fill-current'><path d="m7.375 16.781 1.25-1.562L4.601 12l4.024-3.219-1.25-1.562-5 4a1 1 0 0 0 0 1.562l5 4zm9.25-9.562-1.25 1.562L19.399 12l-4.024 3.219 1.25 1.562 5-4a1 1 0 0 0 0-1.562l-5-4zm-1.649-4.003-4 18-1.953-.434 4-18z"></path></svg>
                            </div>
                            <h3 className="text-xl md:text-2xl text-gray-900 dark:text-white font-bold mb-3">
                                Impact <span className='font-black'>2.0</span>
                            </h3>
                            <p className='text-sm md:text-base text-gray-600 dark:text-gray-300 font-light leading-relaxed'>
                                La mission principale de Tomorrow est de créer une mémorabilité pour les messages publicitaires et de susciter une réaction immédiate. L'Impact 2.0, c'est l'art de générer un ROI exceptionnel tout en établissant les paramètres d'une campagne publicitaire véritablement réussie.
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </div> */}
        </div>
    )
}

export default Services;
